import { useEffect, useMemo, useState } from "react";
import type {
  Checklist,
  ClosingDoc,
  DealFeature,
  Recommendation,
  RecommendationRun,
} from "@closing/shared";
import { api } from "../lib/api";
import {
  acceptRecommendation,
  addSectionReturningId,
  dismissRecommendation,
  watchDealDocs,
  watchLatestRun,
} from "../lib/db";

const FEATURE_LABEL: Record<string, string> = {
  multiple_closings: "Multiple closings",
  tranches: "Tranches",
  daca: "DACA",
  warrants: "Warrants",
  guaranty: "Guaranty",
  registration_rights: "Registration rights",
  transfer_agent_instructions: "TA instructions",
  escrow: "Escrow",
  security_collateral: "Security/collateral",
  pledge: "Pledge",
  intercreditor_subordination: "Intercreditor/subordination",
  prepaid_purchase: "Prepaid purchase (PPP)",
  equity_line: "Equity line",
  board_officer_approvals: "Board/officer approvals",
  side_letter: "Side letter",
  amendments: "Amendments",
  other: "Other",
};

/**
 * "Analyze documents" → Claude detects deal features and recommends
 * checklist items grounded in the docs. Accept places the item into a
 * section (expanding per-closing items); decisions live in the run's
 * `decisions` map — the recommendations themselves are server-immutable.
 */
export default function RecommendationsPanel({
  dealId,
  checklist,
}: {
  dealId: string;
  checklist: Checklist;
}) {
  const [docs, setDocs] = useState<(ClosingDoc & { id: string })[]>([]);
  const [run, setRun] = useState<(RecommendationRun & { id: string }) | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => watchDealDocs(dealId, setDocs), [dealId]);
  useEffect(() => watchLatestRun(dealId, setRun), [dealId]);

  const closingCount = useMemo(() => {
    const f = run?.dealFeatures.find(
      (x) => (x.feature === "multiple_closings" || x.feature === "tranches") && x.present,
    );
    return f?.closingCount ?? null;
  }, [run]);

  async function analyze() {
    setStarting(true);
    setError(null);
    try {
      // Resolves when the run is finished (the server runs it inside the
      // request); watchLatestRun shows the new run doc, "running", meanwhile.
      await api.recommend(dealId);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setStarting(false);
    }
  }

  const presentFeatures = run?.dealFeatures.filter((f) => f.present) ?? [];
  // A run the server never finished (its request was cut off mid-run, e.g. the
  // tab that started it closed) would otherwise stay
  // "running" forever and keep the button disabled. Past STALE_RUN_MS it's
  // treated as failed so the deal can be analyzed again.
  const running = run?.status === "running" && !isStaleRun(run.createdAt);
  const stalled = run?.status === "running" && !running;

  const pending =
    run?.recommendations.filter((r) => !run.decisions?.[r.id]) ?? [];
  const decided =
    run?.recommendations.filter((r) => run.decisions?.[r.id]) ?? [];

  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <p className="text-[13px] text-ink-soft">
          {docs.length > 0
            ? `Analyzes the ${docs.length} uploaded document${docs.length === 1 ? "" : "s"} and recommends checklist items this deal needs.`
            : "Upload deal documents first — then Claude can recommend checklist items based on what the deal is doing."}
        </p>
        <button
          onClick={() => void analyze()}
          disabled={starting || docs.length === 0 || running}
          className="btn-primary flex-none text-xs"
        >
          {running ? "Analyzing…" : starting ? "Starting…" : "Analyze documents"}
        </button>
      </div>
      {error && <p className="mt-3 font-mono text-[11px] text-flag">{error}</p>}
      {stalled && (
        <p className="mt-3 font-mono text-[11px] text-flag">
          The last analysis didn't finish. Run it again.
        </p>
      )}

      {running && (
        <div className="mt-6 rounded-[10px] border border-line bg-white px-5 py-8 text-center">
          <p className="eyebrow animate-pulse text-signature">Reading documents…</p>
          <p className="mt-2 font-mono text-[11px] text-muted">
            Typically 1–2 minutes for a multi-document deal.
          </p>
        </div>
      )}

      {run?.status === "error" && (
        <p className="mt-4 rounded-[10px] border border-line bg-flag-bg/50 px-4 py-3 font-mono text-[11px] text-flag">
          Analysis failed: {run.error}
        </p>
      )}

      {run?.status === "complete" && (
        <>
          {/* Detected features */}
          <div className="mt-6">
            <p className="eyebrow">Detected deal features</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {presentFeatures.length === 0 && (
                <span className="font-mono text-[11px] text-faint">None detected.</span>
              )}
              {presentFeatures.map((f) => (
                <FeatureChip key={f.feature} feature={f} />
              ))}
            </div>
          </div>

          {/* Pending recommendations */}
          <div className="mt-6 space-y-3">
            <p className="eyebrow">
              Recommendations{" "}
              <span className="text-faint">
                ({pending.length} pending · {decided.length} decided)
              </span>
            </p>
            {run.recommendations.length === 0 && (
              <p className="font-mono text-[11px] text-faint">
                Nothing to add — the checklist already covers what the documents show.
              </p>
            )}
            {pending.map((rec) => (
              <RecCard
                key={rec.id}
                rec={rec}
                checklist={checklist}
                closingCount={closingCount}
                onAccept={async (sectionId, newSectionName) => {
                  const targetSectionId = newSectionName
                    ? await addSectionReturningId(dealId, checklist, newSectionName)
                    : sectionId!;
                  await acceptRecommendation(dealId, checklist, run.id, rec, {
                    sectionId: targetSectionId,
                    closingCount,
                  });
                }}
                onDismiss={() => void dismissRecommendation(run.id, rec.id)}
              />
            ))}
            {decided.map((rec) => {
              const decision = run.decisions[rec.id]!;
              return (
                <div
                  key={rec.id}
                  className="flex items-center justify-between gap-3 rounded-[10px] border border-line bg-paper px-4 py-2 opacity-70"
                >
                  <span className="truncate text-[13px] text-ink-soft">{rec.title}</span>
                  <span
                    className={`badge flex-none ${
                      decision.status === "accepted"
                        ? "bg-[#e6f4ea] text-positive"
                        : "bg-neutral-bg text-muted"
                    }`}
                  >
                    {decision.status}
                  </span>
                </div>
              );
            })}
          </div>

          <p className="mt-6 font-mono text-[10px] text-faint">
            Quotes are model-cited from the uploaded documents — verify anything load-bearing.
            Run: {run.model} · {run.usage ? `${Math.round((run.usage.inputTokens + run.usage.outputTokens) / 1000)}k tokens` : ""}
          </p>
        </>
      )}
    </div>
  );
}

function FeatureChip({ feature }: { feature: DealFeature }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      onClick={() => setOpen(!open)}
      className="rounded-full border border-line bg-white px-3 py-1 text-left text-[12px] font-semibold text-ink-soft hover:border-line-strong"
      title="Click for evidence"
    >
      {FEATURE_LABEL[feature.feature] ?? feature.feature}
      {feature.closingCount ? ` ×${feature.closingCount}` : ""}
      {open && (
        <span className="mt-1 block max-w-md font-mono text-[10px] font-normal leading-relaxed text-muted">
          {feature.detail}
          {feature.sourceQuote && (
            <>
              <br />
              <span className="italic">“{feature.sourceQuote}”</span>
              {feature.sourceDoc ? ` — ${feature.sourceDoc}` : ""}
            </>
          )}
        </span>
      )}
    </button>
  );
}

function RecCard({
  rec,
  checklist,
  closingCount,
  onAccept,
  onDismiss,
}: {
  rec: Recommendation;
  checklist: Checklist;
  closingCount: number | null;
  onAccept: (sectionId: string | null, newSectionName: string | null) => Promise<void>;
  onDismiss: () => void;
}) {
  // Deactivated sections are hidden from the checklist and excluded from
  // progress, so accepting an item into one would immediately bury it — offer
  // only active sections as targets.
  const sections = [...checklist.sections]
    .filter((s) => s.active !== false)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const suggested = sections.find((s) => s.name === rec.suggestedSection);
  const [sectionChoice, setSectionChoice] = useState<string>(suggested?.id ?? "__new__");
  const [busy, setBusy] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);

  const copies = rec.perClosing && closingCount && closingCount > 1 ? closingCount : 1;

  async function accept() {
    setBusy(true);
    try {
      await onAccept(
        sectionChoice === "__new__" ? null : sectionChoice,
        sectionChoice === "__new__" ? rec.suggestedSection : null,
      );
    } catch (err) {
      window.alert(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <div className="rounded-[10px] border border-line bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-semibold text-ink">{rec.title}</span>
            {rec.perClosing && copies > 1 && (
              <span className="badge bg-active-bg text-active">×{copies} closings</span>
            )}
            <span
              className={`badge ${
                rec.priority === "high"
                  ? "bg-flag-bg text-flag"
                  : rec.priority === "medium"
                    ? "bg-neutral-bg text-ink-soft"
                    : "bg-paper text-muted"
              }`}
            >
              {rec.priority}
            </span>
            {rec.confidence !== "high" && (
              <span className="font-mono text-[10px] text-faint">
                confidence: {rec.confidence}
              </span>
            )}
          </div>
          {rec.description && (
            <p className="mt-1 text-[12.5px] leading-relaxed text-ink-soft">{rec.description}</p>
          )}
          <button
            onClick={() => setShowEvidence(!showEvidence)}
            className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-muted hover:text-ink"
          >
            {showEvidence ? "hide evidence" : "why?"}
          </button>
          {showEvidence && (
            <div className="mt-2 rounded-md bg-paper px-3 py-2 font-mono text-[11px] leading-relaxed text-ink-soft">
              {rec.rationale}
              {rec.sourceQuote && (
                <p className="mt-1.5 italic text-muted">
                  “{rec.sourceQuote}” — {rec.sourceDoc}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 border-t border-line pt-3">
        <span className="font-mono text-[10px] uppercase tracking-wider text-faint">into</span>
        <select
          value={sectionChoice}
          onChange={(e) => setSectionChoice(e.target.value)}
          className="rounded border border-line bg-white px-2 py-1 text-[12px] text-ink-soft"
        >
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
          {!suggested && <option value="__new__">+ new: “{rec.suggestedSection}”</option>}
        </select>
        <div className="flex-1" />
        <button onClick={onDismiss} disabled={busy} className="btn-secondary text-xs">
          Dismiss
        </button>
        <button onClick={() => void accept()} disabled={busy} className="btn-primary text-xs">
          {busy ? "Adding…" : copies > 1 ? `Accept ×${copies}` : "Accept"}
        </button>
      </div>
    </div>
  );
}

const STALE_RUN_MS = 15 * 60 * 1000;

/** True when a run started more than STALE_RUN_MS ago (createdAt is a Firestore Timestamp). */
function isStaleRun(createdAt: unknown): boolean {
  const ms = (createdAt as { toMillis?: () => number } | null)?.toMillis?.();
  return typeof ms === "number" && Date.now() - ms > STALE_RUN_MS;
}
